import { useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Horizontal auto-scrolling strip.
 *
 *  - Renders `children` inline in a "copy" wrapper.
 *  - If the copy is wider than the viewport, renders it TWICE and animates
 *    a seamless CSS marquee (translateX 0 → -50%).
 *  - Pauses on hover / focus-within so users can click a card.
 *  - Speed is expressed in pixels per second so cadence is consistent
 *    regardless of how many items are inside.
 *
 * Zero external dependencies — the keyframes are injected once per
 * document via a <style> tag, no Tailwind config changes needed.
 */
export default function AutoScrollStrip({ children, speedPxPerSec = 40, className = "", testId }) {
  const outerRef = useRef(null);
  const copyRef = useRef(null);
  const [overflow, setOverflow] = useState(false);
  const [duration, setDuration] = useState(20);

  useLayoutEffect(() => {
    const measure = () => {
      const outer = outerRef.current;
      const copy = copyRef.current;
      if (!outer || !copy) return;
      const copyWidth = copy.scrollWidth;
      const isOverflowing = copyWidth > outer.clientWidth + 4;
      setOverflow(isOverflowing);
      if (isOverflowing) {
        setDuration(Math.max(6, copyWidth / speedPxPerSec));
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (outerRef.current) ro.observe(outerRef.current);
    if (copyRef.current) ro.observe(copyRef.current);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [children, speedPxPerSec]);

  useEffect(() => {
    if (document.getElementById("hunt57-marquee-kf")) return;
    const style = document.createElement("style");
    style.id = "hunt57-marquee-kf";
    style.textContent = `
      @keyframes hunt57-marquee {
        0%   { transform: translate3d(0, 0, 0); }
        100% { transform: translate3d(-50%, 0, 0); }
      }
      .hunt57-marquee-track { animation: hunt57-marquee linear infinite; will-change: transform; }
      .hunt57-marquee-wrap:hover .hunt57-marquee-track,
      .hunt57-marquee-wrap:focus-within .hunt57-marquee-track { animation-play-state: paused; }
    `;
    document.head.appendChild(style);
  }, []);

  const items = Array.isArray(children) ? children : [children];

  return (
    <div
      ref={outerRef}
      data-testid={testId}
      className={`hunt57-marquee-wrap overflow-hidden ${className}`}
    >
      {overflow ? (
        <div
          className="hunt57-marquee-track flex gap-2 w-max"
          style={{ animationDuration: `${duration}s` }}
        >
          <div ref={copyRef} className="flex gap-2 shrink-0">{items}</div>
          <div aria-hidden="true" className="flex gap-2 shrink-0">{items}</div>
        </div>
      ) : (
        <div ref={copyRef} className="flex gap-2 overflow-x-auto pb-1 snap-x snap-mandatory">
          {items}
        </div>
      )}
    </div>
  );
}
